'use client'

import { useEffect, useRef, useState } from 'react'
import {
  areImagesWarm,
  discoveryTileImageSource,
  preloadImages,
} from '@material-bank/mtag-utilities'

type UseDiscoveryRevealParams = {
  // The network request has finished (resolved or errored). Cached data alone must
  // not flip this — that is the whole point of gating the reveal.
  settled: boolean
  // The first row(s) of image URLs the grid will paint. Decoding these before the
  // reveal is what turns the hand-off into a cache hit instead of a pop-in.
  aboveFoldUrls: string[]
}

// Gates the discovery interstitial: `true` only once the network result has settled
// AND its above-the-fold images have decoded, so the overlay lifts onto warm tiles
// with no swap and no pop-in. Resolves immediately (nothing to wait for) when the
// settled result carries no images — an empty or errored set must still reveal.
export function useDiscoveryReveal({
  settled,
  aboveFoldUrls,
}: UseDiscoveryRevealParams): boolean {
  // The tiles render sized sources (SwatchTile), so warm and check those — the
  // raw URLs would be different requests.
  const aboveFoldSources = aboveFoldUrls.map(discoveryTileImageSource)
  // Warm on arrival — the result was already in the cache and the PDP prefetch
  // decoded these tiles — means there is nothing to wait for. Resolving in the
  // initial state rather than in the effect below lets the caller's overlay leave
  // at its floor instead of waiting out another decode.
  const [ready, setReady] = useState(
    () => settled && areImagesWarm(aboveFoldSources),
  )
  // A single one-shot latch: the warm-up fires exactly once. Deliberately not
  // paired with a per-run cleanup flag — a re-render mid-decode (an unmemoized
  // `aboveFoldUrls` yields a fresh array reference every render) would otherwise
  // run the effect cleanup, then early-return on the latch, and the original
  // decode would resolve into a dead cancellation flag — hanging the reveal forever.
  const started = useRef(false)
  // Only a real unmount suppresses the reveal, never an intervening re-render.
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true

    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (started.current || !settled) {
      return
    }

    started.current = true

    if (aboveFoldSources.length === 0) {
      setReady(true)

      return
    }

    void preloadImages(aboveFoldSources).then(() => {
      if (mounted.current) {
        setReady(true)
      }
    })
  }, [settled, aboveFoldSources])

  return ready
}
